/**
 * Task Card Component
 * TASK-359: TaskListPage & Dashboard Integration
 *
 * Displays a single task with quick actions.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  Clock,
  AlertCircle,
  AlertTriangle,
  User,
  Calendar,
  ChevronDown,
  ChevronUp,
  Bell,
  MoreHorizontal,
  Sparkles,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Task, TaskType, TASK_OUTCOME_COPY, getPriorityClass, getTaskTypeLabel } from '../../types/tasks';
import { formatDistanceToNow, isPast, isToday } from 'date-fns';
import { ProvenanceChain, type ProvenanceStep } from '../provenance/ProvenanceChain';
import { toast } from 'sonner';
import { useDockStore } from '../../stores/dockStore';
import { requestCopilotPrompt } from '../dock/requestCopilotPrompt';
import type { PromptHandoff } from '../../stores/createChatStore';
import { FEATURES } from '../../config/api-config';
import { taskService } from '../../services/taskService';
import TaskArtifactList from './TaskArtifactList';
import TaskActivityFeed from './TaskActivityFeed';
import TaskOutcomePicker, { type TaskOutcomeSubmission } from './TaskOutcomePicker';

/**
 * TASK-775 — what "done" looks like, per kind of task.
 *
 * A delegated task frequently arrives with NO statement of the job at all:
 *
 * | Created from | What lands in `description` |
 * |---|---|
 * | a note (`client_notes_routes.py:349`) | nothing — the INSERT has no `description` column |
 * | a life event (`life_event_actions.py:151`) | provenance: "Auto-proposed from the … life event." |
 * | a meeting (`post_meeting_routes_with_tasks.py:136`) | whatever the extractor produced, often "" |
 *
 * So the agent was being told a title and, at best, where the task came from —
 * never what to produce. It behaved accordingly: on a source-of-wealth review it
 * queried the knowledge graph, offered to propose KYC field values, and stopped,
 * taking three more manual turns before a document existed.
 *
 * `task_type` is on every task and needs no migration, so the deliverable is
 * derived from it. This does NOT replace fixing the stored descriptions — but a
 * description fix alone cannot reach the note-derived case, where there is no
 * text to fix.
 */

/**
 * "As a document, attached to this task" — the phrasing matters, and the first
 * attempt got it wrong.
 *
 * v1 said "Produce a WRITTEN source-of-wealth review: …". On a live run the
 * agent did the work properly and in one turn — full review, verification
 * checklist, risk assessment, recommendation — and then typed all of it into
 * the chat, because "written" reads as *in prose*, not *as a file*. Nothing was
 * generated, so nothing was attached, so the auto-attach never ran and the task
 * never left `pending` (TASK-774's transition hangs off a successful attach).
 *
 * Naming the artifact and the attachment is what turns a good answer into a
 * finished task. The `work_task` instruction later in the payload then has
 * something to claim.
 */
const DOCUMENT_DELIVERABLE = (what: string, covers?: string): string =>
  `Create ${what} as a document and attach it to this task.` +
  (covers ? ` It should cover: ${covers}.` : '');
const DELIVERABLE: Partial<Record<TaskType, string>> = {
  follow_up_email: 'Write the email as a draft I can review and send.',
  follow_up_document: DOCUMENT_DELIVERABLE('the document this task calls for'),
  follow_up_call: DOCUMENT_DELIVERABLE('talking points for the call'),
  follow_up_meeting: DOCUMENT_DELIVERABLE('the meeting agenda and the material it needs'),
  follow_up_other: DOCUMENT_DELIVERABLE('the result I asked for'),
  task_review: DOCUMENT_DELIVERABLE(
    'the review',
    'what you checked, what you found, and what I should decide'
  ),
  task_research: DOCUMENT_DELIVERABLE(
    'the research summary',
    'what you found, and where each fact came from'
  ),
  task_compliance: DOCUMENT_DELIVERABLE(
    'the compliance assessment',
    'the applicable requirement, where this client stands against it, and what is still missing'
  ),
  task_admin: DOCUMENT_DELIVERABLE('the completed item'),
  task_general: DOCUMENT_DELIVERABLE('the result I asked for'),
  sow_review: DOCUMENT_DELIVERABLE(
    'the source-of-wealth review',
    'the origin of the funds, whether it is consistent with what we hold on this client, and ' +
      'what still needs documentary verification'
  ),
  crs_recert: DOCUMENT_DELIVERABLE(
    'the CRS recertification assessment',
    'the current position, what has changed, and what the client must confirm'
  ),
  suitability_reassessment: DOCUMENT_DELIVERABLE(
    'the suitability reassessment',
    'what changed, whether the mandate still fits, and what to do if it does not'
  ),
};

/**
 * The message that hands this task to the harness.
 *
 * The TITLE carries the routing: `specialists/router` classifies on the message text, so
 * "draft a letter…" reaches the workbench and "review the allocation…" reaches portfolio —
 * each of which now also carries `work_task` (see `specialists/registry._WORKS_TASKS`).
 * The id is what `work_task` claims; without it the agent can produce an artifact but has
 * nothing to attach it to.
 *
 * TASK-767: returns `{display, payload}`. The PAYLOAD is byte-identical to what it has
 * always been — see the routing note below, and the spec that pins the exact string. The
 * DISPLAY is what appears in the transcript as the advisor's own message: no UUIDs, no
 * `work_task`. Nothing about the wire format changed; only what is rendered did.
 */
function delegationPrompt(task: Task): PromptHandoff {
  // The TITLE LEADS, deliberately. `specialists/router` classifies the whole message, so a
  // prompt that opened with "Work on this task and prepare it for sign-off / Task id: …" read
  // as generic housekeeping and routed to `global` — the one specialist with neither
  // `work_task` nor any authoring tool, which then burned its whole turn budget going nowhere.
  // Leading with what the task actually asks for routes it to a specialist that can do it.
  // TASK-775: the deliverable follows the title and description, never precedes
  // them — the router classifies on the whole message and the title has to keep
  // leading it. It is appended, so the routing-critical opening is byte-identical
  // to what it was before.
  const deliverable = task.task_type ? DELIVERABLE[task.task_type] : undefined;

  const lines = [task.title, ''];
  if (task.description) lines.push(task.description, '');
  lines.push('Do this for me and prepare it for my sign-off.');
  if (deliverable) lines.push(deliverable);
  lines.push(`Task id: ${task.id}`);
  if (task.client_id) lines.push(`Client id: ${task.client_id}`);
  lines.push(
    '',
    'When the work is done, call work_task to claim the task, attach what you produced, and ' +
      'leave it in review. If you cannot finish it, say what stopped you.',
  );

  // What the advisor actually asked for — the task, the sign-off request, and
  // what they are going to get back. The description is deliberately kept: it is
  // theirs, unlike the ids.
  const shown = [task.title, ''];
  if (task.description) shown.push(task.description, '');
  shown.push('Do this for me and prepare it for my sign-off.');
  if (deliverable) shown.push(deliverable);

  return { display: shown.join('\n'), payload: lines.join('\n') };
}

// TASK-683: why a task exists, generalised past the single life_event case.
const SOURCE_LABELS: Record<string, string> = {
  life_event: 'From life event',
  meeting: 'From a meeting',
  meeting_transcript: 'From a meeting',
  dictation: 'From a dictation',
  alert: 'From an alert',
  drift: 'From portfolio drift',
  data_correction: 'Reported data issue',
  manual: 'Created by hand',
  email: 'From an email',
};

function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? `From ${source.replace(/_/g, ' ')}`;
}

/**
 * The task's stored citation as a chain step — or null when it has none.
 * A task created by hand has no transcript sentence, and inventing one would be the exact
 * failure this ticket exists to remove.
 */
function taskProvenance(task: Task): ProvenanceStep | null {
  const excerpt = task.transcript_excerpt?.trim();
  if (!excerpt) return null;
  return {
    excerpt,
    locator:
      typeof task.transcript_line_number === 'number'
        ? { transcript_line: task.transcript_line_number }
        : null,
    origin: task.source === 'dictation' ? 'dictation' : 'meeting',
    sourceLabel: task.source ? sourceLabel(task.source) : 'Transcript',
    confidence: task.confidence_score ?? null,
    // TASK-683 review: the quote was a dead end. The task already knows its session, and the
    // excerpt was verified verbatim against that transcript, so the surrounding utterances
    // are one click away — the RM can see what was said either side of the sentence.
    sessionId: task.session_id ?? null,
  };
}

/**
 * TASK-735 — the audit spine, fetched only while the card is expanded. Mounting this
 * component IS the `enabled` gate: collapsed cards never run the query, so list surfaces
 * pay nothing for it.
 */
const TaskSpine: React.FC<{ taskId: string }> = ({ taskId }) => {
  const detail = useQuery({
    queryKey: ['task-detail', taskId],
    queryFn: () => taskService.getTaskDetail(taskId),
  });

  if (detail.isLoading) {
    return <p className="text-xs text-gray-500">Loading the audit trail…</p>;
  }
  if (detail.isError || !detail.data) {
    // Never blank: "nothing here" and "we could not read it" are different statements.
    return (
      <p data-testid="task-spine-error" className="text-xs text-amber-800">
        This task&apos;s audit trail could not be loaded.
      </p>
    );
  }

  return (
    <>
      <section aria-label="Artifacts">
        <h4 className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Artifacts</h4>
        <div className="mt-1">
          <TaskArtifactList
            artifacts={detail.data.artifacts}
            bindings={detail.data.bindings}
            taskId={taskId}
            // TASK-743: binding mutations reload the spine so role/withdrawal state is
            // always the server's, never an optimistic guess.
            onMutated={() => detail.refetch()}
          />
        </div>
      </section>
      <section aria-label="Activity">
        <h4 className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Activity</h4>
        <div className="mt-1">
          <TaskActivityFeed events={detail.data.events} />
        </div>
      </section>
    </>
  );
};

interface TaskCardProps {
  task: Task;
  /**
   * TASK-922: `outcome` travels with the completion for any task carrying `subject_key` —
   * the server rejects those without one (400 `outcome_required`). Optional in the type
   * because a plain task still completes with nothing to say.
   */
  onComplete?: (taskId: string, submission?: TaskOutcomeSubmission) => void | Promise<unknown>;
  onSnooze?: (taskId: string, snoozeUntil: string) => void;
  onEdit?: (task: Task) => void;
  isCompact?: boolean;
}

const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onComplete,
  onSnooze,
  onEdit,
  isCompact = false,
}) => {
  const navigate = useNavigate();
  const [showActions, setShowActions] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [pickingOutcome, setPickingOutcome] = useState(false);
  const [completing, setCompleting] = useState(false);
  const provenance = taskProvenance(task);

  // TASK-922: work raised from a signal cannot be closed with a shrug. The server has
  // enforced this since TASK-724; the card is where the advisor gets to answer.
  const needsOutcome = Boolean(task.subject_key);

  // Still actionable: not already closed. Drives the complete/snooze affordances so a
  // `review` task (TASK-684) can actually be signed off, rather than being a dead end.
  const isOpen = task.status !== 'completed' && task.status !== 'cancelled';
  // Narrower than `isOpen`, which includes `review`: a task in review has ALREADY been worked
  // by the harness and is waiting on the advisor. Re-delegating it would stack a second
  // agent's work on the first's — the same thing `work_task` refuses server-side.
  const isDelegatable = task.status === 'pending' || task.status === 'in_progress';

  // Determine if task is overdue
  const isOverdue = task.due_date && isPast(new Date(task.due_date)) && task.status === 'pending';
  const isDueToday = task.due_date && isToday(new Date(task.due_date));

  // Format due date
  const formatDueDate = () => {
    if (!task.due_date) return null;
    const date = new Date(task.due_date);
    if (isOverdue) {
      return `Overdue by ${formatDistanceToNow(date)}`;
    }
    if (isDueToday) {
      return 'Due today';
    }
    return `Due ${formatDistanceToNow(date, { addSuffix: true })}`;
  };

  // Handle quick complete
  const handleComplete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onComplete) return;
    if (needsOutcome) {
      setPickingOutcome(true);
      return;
    }
    // `void ... catch`: DoColumn hands back `mutateAsync`'s promise so the picker can stay
    // open on a rejection, and an unhandled rejection here would be the price of that on the
    // plain path, where `mutate()` used to swallow it. The caller owns the message.
    void Promise.resolve(onComplete(task.id)).catch(() => {});
  };

  // The picker holds typed reasoning, so it must not vanish on a completion that FAILED —
  // the artifact gate 409s and the outcome gate 400s are both live on this path, and losing
  // a rationale to one of them means the advisor retypes it or (more likely) does not.
  // Closes only once the parent's promise resolves; a `void` return closes immediately,
  // which is the old fire-and-forget behaviour for callers that have no promise to give.
  const handleOutcome = async (submission: TaskOutcomeSubmission) => {
    try {
      setCompleting(true);
      await onComplete?.(task.id, submission);
      setPickingOutcome(false);
    } catch {
      // The caller owns the message; this only decides whether the form stays open.
    } finally {
      setCompleting(false);
    }
  };

  // Navigate to client if linked
  const handleClientClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (task.client_id) {
      navigate(`/clients/${task.client_id}`);
    }
  };

  // Hand the task to the harness. The board had no route to the AI at all: an advisor could
  // only complete, snooze or edit — the work itself was always theirs.
  // TASK-713: routed to the dock — the canvas InputDock on /dashboard (which now
  // actually sends it; the old floating chat was hidden there and the button was
  // a silent no-op), the global dock everywhere else.
  const handleDelegate = (e: React.MouseEvent) => {
    e.stopPropagation();
    // TASK-769: delegating NAMES the thread this work owns, so the conversation
    // follows the task across placements instead of dying with the route. The
    // artifact, the workbench rail and the review all attach to this thread.
    //
    // TASK-788 suppressed this on /dashboard, because the canvas InputDock's
    // conversation lived in one unscoped singleton and a `task:<id>` thread
    // opened there never received a message — the chip followed the advisor
    // around advertising an empty transcript while the real conversation sat on
    // the canvas. TASK-795 gave the canvas placement a scoped store, so the
    // thread is opened on every placement again.
    if (FEATURES.ENABLE_TASK_THREADS) {
      useDockStore.getState().openTaskThread({
        taskId: task.id,
        title: task.title,
        clientId: task.client_id ?? null,
      });
    }
    // The PROMPT still routes by placement: on /dashboard the copilot IS the
    // canvas InputDock (TASK-712), which reads canvasStore. What TASK-795 lifted
    // is the thread suppression, not this routing — handing the prompt to
    // `useDockStore` here would send it to a dock that never mounts. TASK-816
    // moved that rule into one shared helper, so every surface that delegates
    // agrees on it.
    if (!requestCopilotPrompt(delegationPrompt(task), window.location.pathname)) {
      toast.error('Open the copilot here to work on this with AI');
    }
  };

  // Compact version for dashboard
  if (isCompact) {
    return (
      <div className={`rounded-lg ${isOverdue ? 'bg-red-50' : ''}`}>
      <div
        className={`
          flex items-start gap-3 p-3 rounded-lg transition-colors cursor-pointer
          ${isOverdue ? 'hover:bg-red-100' : 'hover:bg-gray-50'}
        `}
        onClick={() => onEdit?.(task)}
      >
        <div className="flex-shrink-0 mt-0.5">
          {task.status === 'completed' ? (
            <CheckCircle className="w-4 h-4 text-green-600" />
          ) : isOverdue ? (
            <AlertCircle className="w-4 h-4 text-red-600" />
          ) : (
            <Clock className="w-4 h-4 text-gray-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{task.title}</p>
          {task.client_id && (
            <p className="text-xs text-gray-500">{task.client_id}</p>
          )}
          {task.due_date && (
            <p className={`text-xs font-medium mt-1 ${isOverdue ? 'text-red-600' : 'text-gray-500'}`}>
              {formatDueDate()}
            </p>
          )}
        </div>
        {/* TASK-684: `review` means "the AI finished this, sign it off" — and signing off
            IS completing. Gating on 'pending' made review a dead end: a task could enter
            it and never leave through the UI. Anything not already closed can be closed. */}
        {isOpen && onComplete && (
          <button
            onClick={handleComplete}
            className="flex-shrink-0 p-1 text-gray-400 hover:text-green-600 transition-colors"
            title={needsOutcome ? 'Complete — say what happened' : 'Mark as complete'}
          >
            <CheckCircle className="w-4 h-4" />
          </button>
        )}
      </div>
      {/* Inside the card, not a modal: the advisor is answering about THIS row and the
          title has to stay readable while they choose. */}
      {pickingOutcome && (
        <div className="px-3 pb-3">
          <TaskOutcomePicker
            task={task}
            busy={completing}
            onConfirm={handleOutcome}
            onCancel={() => setPickingOutcome(false)}
          />
        </div>
      )}
      </div>
    );
  }

  // Full version for task list
  return (
    <div
      className={`
        bg-white rounded-lg border shadow-sm transition-all
        ${isOverdue ? 'border-red-200' : 'border-gray-200'}
        ${task.status === 'completed' ? 'opacity-75' : ''}
      `}
    >
      {/* Main Content */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Checkbox / Status Icon */}
          <button
            onClick={handleComplete}
            disabled={task.status === 'completed'}
            aria-label={task.status === 'completed' ? 'Task completed' : 'Mark task complete'}
            className={`
              flex-shrink-0 mt-0.5 transition-colors
              ${task.status === 'completed'
                ? 'text-green-600 cursor-default'
                : 'text-gray-300 hover:text-green-500 cursor-pointer'
              }
            `}
          >
            <CheckCircle className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Title Row */}
            <div className="flex items-start justify-between gap-2">
              <h3
                className={`
                  text-sm font-medium
                  ${task.status === 'completed' ? 'text-gray-500 line-through' : 'text-gray-900'}
                `}
              >
                {task.title}
              </h3>

              {/* Priority Badge */}
              <span
                className={`
                  flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full
                  ${getPriorityClass(task.priority)}
                `}
              >
                {task.priority}
              </span>
            </div>

            {/* Meta Row */}
            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
              {/* Task Type */}
              {task.task_type && (
                <span className="inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                  {getTaskTypeLabel(task.task_type)}
                </span>
              )}

              {/* TASK-576/683: why this task exists. Any recorded origin shows, not just
                  life_event — "manual" is an answer too, and an unknown one is honest. */}
              {task.source && (
                <span
                  data-testid="task-source"
                  className={`inline-flex items-center gap-1 ${
                    task.source === 'life_event' ? 'text-amber-700' : ''
                  }`}
                  title={
                    task.related_entity_id
                      ? `${sourceLabel(task.source)} (${task.related_entity_id})`
                      : sourceLabel(task.source)
                  }
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      task.source === 'life_event' ? 'bg-amber-500' : 'bg-gray-400'
                    }`}
                  />
                  {sourceLabel(task.source)}
                </span>
              )}

              {/* Due Date */}
              {task.due_date && (
                <span
                  className={`
                    inline-flex items-center gap-1
                    ${isOverdue ? 'text-red-600 font-medium' : isDueToday ? 'text-amber-600 font-medium' : ''}
                  `}
                >
                  <Calendar className="w-3 h-3" />
                  {formatDueDate()}
                </span>
              )}

              {/* Reminder */}
              {task.reminder_datetime && !task.reminder_sent && (
                <span className="inline-flex items-center gap-1 text-blue-600">
                  <Bell className="w-3 h-3" />
                  Reminder set
                </span>
              )}

              {/* Client Link */}
              {task.client_id && (
                <button
                  onClick={handleClientClick}
                  className="inline-flex items-center gap-1 hover:text-blue-600 transition-colors"
                >
                  <User className="w-3 h-3" />
                  View client
                </button>
              )}

              {/* Hand it to the AI, while it is still work nobody has done. */}
              {isDelegatable && (
                <button
                  onClick={handleDelegate}
                  data-testid="task-delegate"
                  aria-label={`Work on ${task.title} with AI`}
                  className="inline-flex items-center gap-1 font-medium text-amber-700 hover:text-amber-800 transition-colors"
                >
                  <Sparkles className="w-3 h-3" />
                  Work on this with AI
                </button>
              )}
            </div>

            {/* Details (expandable): description + the audit spine (TASK-735). The toggle
                no longer requires a description — every task has a trail. */}
            <div className="mt-2">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                aria-expanded={isExpanded}
                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-3 h-3" />
                    Hide details
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3 h-3" />
                    Show details
                  </>
                )}
              </button>
              {isExpanded && (
                <div className="mt-2 space-y-3">
                  {task.description && (
                    <p className="text-sm text-gray-600">{task.description}</p>
                  )}
                  <TaskSpine taskId={task.id} />
                </div>
              )}
            </div>

            {/* TASK-683: the sentence this task came from. Rendered by the same component
                the canvas uses, so a citation reads identically wherever it appears. */}
            {provenance && (
              <div
                data-testid="task-provenance"
                className="mt-3 rounded-md border border-gray-100 bg-gray-50 p-2"
              >
                <ProvenanceChain steps={[provenance]} />
              </div>
            )}

            {/* TASK-702: an agent that could not finish leaves the task in `in_progress` —
                `blocked` is deliberately not a third status — so without this the board shows
                an idle-looking row and the reason stays buried in `task_events.detail`. */}
            {task.blocked_by && (
              <div
                data-testid="task-blocked"
                className="mt-3 flex items-start gap-1.5 rounded-md border border-orange-200 bg-orange-50 p-2 text-xs text-orange-900"
              >
                <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" aria-hidden="true" />
                <span>
                  <strong>Blocked:</strong> {task.blocked_by}
                </span>
              </div>
            )}
          </div>

          {/* Actions Menu */}
          <div className="relative">
            <button
              onClick={() => setShowActions(!showActions)}
              aria-label="Task actions"
              aria-expanded={showActions}
              className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>

            {showActions && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowActions(false)}
                />
                <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                  <button
                    onClick={() => {
                      onEdit?.(task);
                      setShowActions(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Edit task
                  </button>
                  {isOpen && (
                    <>
                      <button
                        onClick={() => {
                          // Snooze for 1 day
                          const tomorrow = new Date();
                          tomorrow.setDate(tomorrow.getDate() + 1);
                          tomorrow.setHours(9, 0, 0, 0);
                          onSnooze?.(task.id, tomorrow.toISOString());
                          setShowActions(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Snooze 1 day
                      </button>
                      <button
                        onClick={() => {
                          // Snooze for 1 week
                          const nextWeek = new Date();
                          nextWeek.setDate(nextWeek.getDate() + 7);
                          nextWeek.setHours(9, 0, 0, 0);
                          onSnooze?.(task.id, nextWeek.toISOString());
                          setShowActions(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Snooze 1 week
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* TASK-922: the same picker the compact card uses, so the Do column and the task
            list cannot answer the server differently. */}
        {pickingOutcome && (
          <TaskOutcomePicker
            task={task}
            busy={completing}
            onConfirm={handleOutcome}
            onCancel={() => setPickingOutcome(false)}
          />
        )}
      </div>

      {/* TASK-922: what was recorded, once it has been. `outcome` is on every task the API
          returns and was rendered nowhere — a completion whose reason exists only in the
          audit trail is a reason nobody reads. */}
      {task.outcome && (
        <div
          data-testid="task-outcome"
          className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-600"
        >
          <span className="text-gray-400">Outcome: </span>
          {TASK_OUTCOME_COPY[task.outcome]?.label ?? task.outcome}
          {task.outcome_rationale && (
            <span className="text-gray-500"> — {task.outcome_rationale}</span>
          )}
          {/* The date the advisor picked. Deferring and then being shown nothing about WHEN
              is the one outcome where the card has to answer back, and it did not. */}
          {task.outcome === 'deferred' && task.snoozed_until && (
            <span data-testid="task-deferred-until" className="block text-gray-500">
              Back on {new Date(task.snoozed_until).toLocaleString()}
            </span>
          )}
        </div>
      )}

      {/* Status Bar */}
      {task.status === 'completed' && task.completed_at && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 rounded-b-lg">
          <p className="text-xs text-gray-500">
            Completed {formatDistanceToNow(new Date(task.completed_at), { addSuffix: true })}
          </p>
        </div>
      )}
    </div>
  );
};

export default TaskCard;
